<template>
  <div>
    <v-row dense>
      <v-col
        class="py-0"
        cols="12"
      >
        <PublicNotice
          v-if="program.public_notice"
          :public-notice="program.public_notice"
          small
        />

        <div
          v-if="enrollmentCount != null"
          class="d-flex align-start my-3"
        >
          <v-icon
            class="me-3"
            color="primary"
            icon="chair_alt"
            size="24"
          />
          <template v-if="$role == 'specialist'">
            <span
              @click="updateQuery({ tab: 'students', programId: program.id })"
              class="c-primary underlined pointer"
            >
              {{ enrollmentCount }} {{ $bct('enrolled') }}
            </span>
          </template>
          <template v-else>
            <router-link
              :to="{
                name: 'EnrollmentIndex',
                query: { providerId: $route.params.providerId, programId: program.id },
              }"
            >
              {{ enrollmentCount }} {{ $bct('enrolled') }}
            </router-link>
          </template>

          <template v-if="seatCount != null && seatCount > 0">
            <div class="ms-2">
              <span class="me-1">/</span>
              {{ seatCount }} {{ $bct('seats') }}
            </div>
          </template>
        </div>

        <div class="d-flex align-start my-3">
          <v-icon
            class="me-3"
            color="primary"
            icon="child_care"
            size="24"
          />
          <div
            v-if="program.grade_max"
            class="d-inline-block fs-16 fw-400"
          >
            {{ $bct('Grades') }}: {{ $bct(gradeMinText) }} - {{ $bct(gradeMaxText) }}
          </div>
          <div
            v-if="!program.grade_max && program.age_max"
            class="d-inline-block fs-16 fw-400"
          >
            {{ $bct('Ages:') }} {{ ageMinText }} - {{ ageMaxText }}
          </div>
          <div
            v-if="!program.grade_max && !program.age_max"
            class="d-inline-block fs-16 fw-400"
          >
            {{ $bct('Age groups not available') }}
          </div>
        </div>

        <div class="d-flex align-start my-3">
          <v-icon
            class="me-3"
            color="primary"
            icon="date_range"
            size="24"
          />

          <div
            v-if="program.hours_description && program.hours_description.length > 0"
            class="d-inline-block"
          >
            <div
              v-if="showSeason"
              v-text="$bct(program.season)"
              class="fs-16 fw-400 mb-1"
              data-testid="program-season"
            />
            <HoursChunks :chunks="program.hours_chunks" />
          </div>

          <div
            v-else
            class="d-inline-block"
          >
            <div class="fs-16 fw-400">
              <span
                v-if="showSeason"
                v-text="$bct(program.season) + ' - '"
                data-testid="program-season"
              />
              <span>{{
                $bct(showSeason ? 'same as location hours' : 'Same as location hours')
              }}</span>
            </div>
          </div>

          <div
            v-if="!program.regular_hours && !program.hours_description"
            class="d-inline-block c-light-black fs-16 fw-400"
          >
            <div
              v-if="showSeason"
              v-text="$bct(program.season)"
              class="fs-16 fw-400"
              data-testid="program-season"
            />
            <div v-else>
              {{ $bct('No program schedule information') }}
            </div>
          </div>
        </div>

        <div class="d-flex align-start my-3">
          <v-icon
            class="me-3"
            color="primary"
            icon="monetization_on"
            size="24"
          />

          <div
            v-if="program.is_free"
            class="d-inline-block"
          >
            <span class="fw-400 fs-16">
              {{ $bct('Free') }}
            </span>
            <span
              v-if="program.is_donation_based"
              class="fs-16 fw-400"
            >
              {{ `- ${$bct('donations accepted')}` }}
            </span>
          </div>

          <div
            v-else
            class="d-inline-block"
          >
            <template v-if="hasRates">
              <div
                v-if="program.rate_hourly"
                class="fs-16 fw-400"
              >
                {{ currency(program.rate_hourly) }} {{ $bct('paid hourly') }}
              </div>
              <div
                v-if="program.rate_daily"
                class="fs-16 fw-400"
              >
                {{ currency(program.rate_daily) }} {{ $bct('paid daily') }}
              </div>
              <div
                v-if="program.rate_weekly"
                class="fs-16 fw-400"
              >
                {{ currency(program.rate_weekly) }} {{ $bct('paid weekly') }}
              </div>
              <div
                v-if="program.rate_monthly"
                class="fs-16 fw-400"
              >
                {{ currency(program.rate_monthly) }} {{ $bct('paid monthly') }}
              </div>
              <div
                v-if="program.rate_bimonthly"
                class="fs-16 fw-400"
              >
                {{ currency(program.rate_bimonthly) }} {{ $bct('paid twice a month') }}
              </div>
              <div
                v-if="program.rate_biweekly"
                class="fs-16 fw-400"
              >
                {{ currency(program.rate_biweekly) }} {{ $bct('paid every two weeks') }}
              </div>
            </template>

            <div
              v-if="!hasRates && program.rate"
              class="fw-400 fs-16"
            >
              {{ currency(program.rate) }} {{ programRateText }}
            </div>

            <div
              v-if="!hasRates && !program.rate"
              class="fs-16 fw-400 c-light-black"
            >
              {{ $bct('No cost information') }}
            </div>
          </div>
        </div>

        <div class="d-flex align-start my-3">
          <v-icon
            class="me-3"
            color="primary"
            icon="person_pin"
            size="24"
          />

          <div
            v-if="!program.contact_name"
            class="d-inline-block fs-16 fw-400 c-light-black"
          >
            {{ $bct('No program contact') }}
          </div>

          <div
            v-else
            class="d-inline-block"
          >
            <div class="fs-16 fw-400">
              {{ `${$bct('Contact')} ${program.contact_name}` }}
            </div>
            <div
              v-text="
                [program.contact_email, program.contact_phone].filter((item) => item).join(' | ')
              "
              class="fs-16 fw-400"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row
      v-if="program.description"
      dense
    >
      <v-col>
        <div
          @click="descriptionVisible = !descriptionVisible"
          id="manager_program_description"
          class="c-primary underlined pointer fs-16 fw-400"
          tracked
        >
          {{ $bct('Description') }}
        </div>
      </v-col>
    </v-row>

    <v-row
      v-if="descriptionVisible && program.description"
      dense
    >
      <v-col>
        <div
          v-text="$bct(program.description)"
          class="fs-16 fw-400"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';

import { currency } from '@/plugins/filters';
import HoursChunks from '@/public/components/provider/HoursChunks.vue';
import PublicNotice from '@/shared/components/PublicNotice.vue';
import useAgeCalculations from '@/shared/composables/useAgeCalculations';
import useAssets from '@/shared/composables/useAssets';
import useRouterHelper from '@/shared/composables/useRouterHelper';

const { assets } = useAssets();
const { monthsToAgeString } = useAgeCalculations();
const { updateQuery } = useRouterHelper();
const store = useStore();
const $bct = inject('translator');

const props = defineProps({
  dashboard: {
    type: Object,
    default: null,
  },
  program: {
    type: Object,
    default: null,
  },
});

const descriptionVisible = ref(false);

// Gate residual season values when the schema field is disabled until data is cleaned up -- see CARE-4555 notes
const showSeason = computed(() => {
  const seasonProperty =
    store.state.schemas[props.program.schema_id]?.definition?.properties?.season;
  return seasonProperty?.enabled === true && !!props.program.season;
});

const ageMaxText = computed(() => {
  return monthsToAgeString(props.program.age_max);
});

const ageMinText = computed(() => {
  return monthsToAgeString(props.program.age_min);
});

const enrollmentCount = computed(() => {
  if (!props.dashboard) return null;

  return (
    props.dashboard.enrollment_program_groups.find((group) => group.program_id === props.program.id)
      ?.count || 0
  );
});

const gradeMaxText = computed(() => {
  return assets.programs.grades[props.program.grade_max].text;
});

const gradeMinText = computed(() => {
  return assets.programs.grades[props.program.grade_min].text;
});

const hasRates = computed(() => {
  return (
    props.program.rate_hourly ||
    props.program.rate_daily ||
    props.program.rate_weekly ||
    props.program.rate_monthly ||
    props.program.rate_bimonthly ||
    props.program.rate_biweekly
  );
});

const programRateText = computed(() => {
  return props.program.period
    ? [$bct('paid'), $bct(props.program.period).toLowerCase()].join(' ')
    : '';
});

const seatCount = computed(() => {
  if (!props.dashboard) return null;

  return (
    props.dashboard.enrollment_program_groups.find((group) => group.program_id === props.program.id)
      ?.seats || 0
  );
});
</script>
